import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, DollarSign, Dumbbell, Hash, MoreHorizontal, Menu, X } from 'lucide-react';
import {
  financialCalculators,
  fitnessCalculators,
  mathCalculators,
  otherCalculators
} from '../data/mock';
import LanguageSelector from './LanguageSelector';
import TranslatedText from './TranslatedText';

// Desktop Dropdown Menu
const DropdownMenu = ({ title, icon: Icon, calculators, isOpen, onToggle }) => {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className="flex items-center gap-1 px-3 py-2 text-gray-300 hover:text-cyan-400 transition-colors text-sm font-medium"
      >
        <Icon className="w-4 h-4" />
        <span><TranslatedText>{title}</TranslatedText></span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
          {calculators.map((calc, index) => (
            <Link
              key={index}
              to={calc.path}
              className="block px-4 py-2 text-gray-300 hover:bg-[#2a2a2a] hover:text-cyan-400 transition-colors text-sm border-b border-[#2a2a2a] last:border-b-0"
              onClick={onToggle}
            >
              <TranslatedText>{calc.name}</TranslatedText>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// Mobile Accordion Menu
const MobileAccordion = ({ title, icon: Icon, calculators, isOpen, onToggle, onLinkClick }) => {
  return (
    <div className="border-b border-[#2a2a2a]">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full px-4 py-3 text-gray-300 hover:text-cyan-400 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5" />
          <span className="font-medium"><TranslatedText>{title}</TranslatedText></span>
        </div>
        <ChevronRight className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="bg-[#0d0d0d] pb-2">
          {calculators.map((calc, index) => (
            <Link
              key={index}
              to={calc.path}
              className="block px-8 py-2 text-gray-400 hover:text-cyan-400 transition-colors text-sm"
              onClick={onLinkClick}
            >
              <TranslatedText>{calc.name}</TranslatedText>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState(null);

  const toggleMenu = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  const closeMenus = () => {
    setOpenMenu(null);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setMobileAccordion(null);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileAccordion(null);
  };

  const toggleMobileAccordion = (menu) => {
    setMobileAccordion(mobileAccordion === menu ? null : menu);
  };

  return (
    <header className="bg-[#0d0d0d] border-b border-[#2a2a2a] relative">
      <div className="max-w-6xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Logo - Links to Home */}
          <Link to="/home" className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition-opacity" onClick={closeMobileMenu}>
            <img src="/logo.png" alt="PercentageCalculatorPlus" className="h-12 md:h-[70px] lg:h-[84px] w-auto" />
            <span className="text-white text-sm md:text-lg lg:text-xl font-bold hidden sm:block">Percentage Calculator Plus</span>
          </Link>

          {/* Desktop Navigation Menu - Hidden on mobile/tablet */}
          <nav className="hidden lg:flex items-center gap-1">
            <DropdownMenu
              title="Financial"
              icon={DollarSign}
              calculators={financialCalculators}
              isOpen={openMenu === 'financial'}
              onToggle={() => toggleMenu('financial')}
            />
            <DropdownMenu
              title="Fitness"
              icon={Dumbbell}
              calculators={fitnessCalculators}
              isOpen={openMenu === 'fitness'}
              onToggle={() => toggleMenu('fitness')}
            />
            <DropdownMenu
              title="Math"
              icon={Hash}
              calculators={mathCalculators}
              isOpen={openMenu === 'math'}
              onToggle={() => toggleMenu('math')}
            />
            <DropdownMenu
              title="Other"
              icon={MoreHorizontal}
              calculators={otherCalculators}
              isOpen={openMenu === 'other'}
              onToggle={() => toggleMenu('other')}
            />
            
            {/* Language Selector */}
            <div className="ml-2">
              <LanguageSelector />
            </div>
          </nav>

          {/* Mobile/Tablet Controls */}
          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSelector />
            <button
              onClick={toggleMobileMenu}
              className="p-2 text-gray-300 hover:text-cyan-400 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Slide-out Menu */}
      {mobileMenuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={closeMobileMenu}
          />
          
          {/* Menu Panel */}
          <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-[#1a1a1a] z-50 lg:hidden overflow-y-auto shadow-xl">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-[#2a2a2a]">
              <span className="text-white font-bold">Menu</span>
              <button
                onClick={closeMobileMenu}
                className="p-2 text-gray-300 hover:text-cyan-400 transition-colors"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              <MobileAccordion
                title="Financial"
                icon={DollarSign}
                calculators={financialCalculators}
                isOpen={mobileAccordion === 'financial'}
                onToggle={() => toggleMobileAccordion('financial')}
                onLinkClick={closeMobileMenu}
              />
              <MobileAccordion
                title="Fitness"
                icon={Dumbbell}
                calculators={fitnessCalculators}
                isOpen={mobileAccordion === 'fitness'}
                onToggle={() => toggleMobileAccordion('fitness')}
                onLinkClick={closeMobileMenu}
              />
              <MobileAccordion
                title="Math"
                icon={Hash}
                calculators={mathCalculators}
                isOpen={mobileAccordion === 'math'}
                onToggle={() => toggleMobileAccordion('math')}
                onLinkClick={closeMobileMenu}
              />
              <MobileAccordion
                title="Other"
                icon={MoreHorizontal}
                calculators={otherCalculators}
                isOpen={mobileAccordion === 'other'}
                onToggle={() => toggleMobileAccordion('other')}
                onLinkClick={closeMobileMenu}
              />
            </div>

            {/* Quick Links */}
            <div className="p-4 border-t border-[#2a2a2a]">
              <Link
                to="/home"
                className="block py-2 text-gray-300 hover:text-cyan-400 transition-colors"
                onClick={closeMobileMenu}
              >
                Home
              </Link>
              <Link
                to="/sitemap"
                className="block py-2 text-gray-300 hover:text-cyan-400 transition-colors"
                onClick={closeMobileMenu}
              >
                All Calculators
              </Link>
              <Link
                to="/about"
                className="block py-2 text-gray-300 hover:text-cyan-400 transition-colors"
                onClick={closeMobileMenu}
              >
                About
              </Link>
              <Link
                to="/contact"
                className="block py-2 text-gray-300 hover:text-cyan-400 transition-colors"
                onClick={closeMobileMenu}
              >
                Contact
              </Link>
            </div>
          </div>
        </>
      )}

      {/* Desktop Overlay to close menus when clicking outside */}
      {openMenu && (
        <div 
          className="fixed inset-0 z-40 hidden lg:block" 
          onClick={closeMenus}
        />
      )}
    </header>
  );
};

export default Header;
